import React, { useEffect, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';

import Badge from '../../../components/Badge';
import DataTable from '../../../components/DataTable';
import Modal from '../../../components/Shell/Modal';
import {
	TemplateBoolean,
	TemplateDropdown,
	TemplateField,
	TemplateReactSelect,
} from '../../../components/Attributes/Attributes_old';

import Button from '../../../components/Button';
import useModal from '../../../hooks/useModal';
import usePrevious from '../../../hooks/usePrevious';
import useAsync from '../../../hooks/useAsync';

import {
	addCategoryField,
	deleteCategory,
	removeCategoryField,
} from '../../../api/categories';

const Categories = ({ module, onRefresh }) => {
	const [form, setForm] = useState({});
	const [isAddFieldOpen, setAddFieldOpen] = useState(false);
	const [isAddToCategoryOpen, setAddToCategoryOpen] = useState(false);
	const [isRemoveFieldOpen, setRemoveFieldOpen] = useState(false);

	const { execute: onDeleteCategory } = useAsync(deleteCategory, {
		immediate: false,
	});

	const { execute: onAddField } = useAsync(addCategoryField, {
		immediate: false,
	});

	const { execute: onRemoveField } = useAsync(removeCategoryField, {
		immediate: false,
	});

	const { open: addCategoryOpen, setOpen: setAddCategoryOpen } =
		useModal('addCategoryModal');

	const {
		open: deleteModalOpen,
		setOpen: setDeleteOpen,
		setClose: setDeleteClose,
	} = useModal('deleteModal');

	const availableFields = module?.attribute_fields?.filter(
		(field) => !field?.isActive
	);

	const prevCategoryOpen = usePrevious(addCategoryOpen);

	useEffect(() => {
		if (!addCategoryOpen && prevCategoryOpen) {
			onRefresh({ id: module?.id }, null, { silent: true });
		}
	});

	return (
		<>
			<DataTable
				columns={[
					{
						header: 'NAME',
						accessorKey: 'name',
						cell: (info) => {
							return <div className="w-36">{info?.getValue() || null}</div>;
						},
					},
					{
						header: 'CUSTOM FIELDS',
						accessorKey: 'attribute_fields',
						wrap: true,
						cell: (info) => {
							return (
								<>
									{info?.getValue()?.map((field) => (
										<Badge
											isBeta
											hasDot={false}
											className={field?.isDefault ? '' : 'cursor-pointer'}
											onClick={() =>
												!field?.isDefault
													? setRemoveFieldOpen({
															categoryId: info?.row?.original?.id,
															fieldId: field?.id,
													  })
													: {}
											}
										>
											{field?.label}
										</Badge>
									))}
									<Badge
										isBeta
										hasDot={false}
										className="cursor-pointer"
										onClick={() =>
											setAddToCategoryOpen({
												id: info?.row?.original?.id,
												skip: info?.getValue()?.map((field) => field.id),
											})
										}
									>
										<PlusIcon className="w-3 h-3 text-gray-600" />{' '}
										{info?.getValue()?.length ? '' : 'Add Field'}
									</Badge>
								</>
							);
						},
						enableSorting: false,
					},
				]}
				data={{
					rows:
						module?.categories?.sort((a, b) => a.name.localeCompare(b.name)) ||
						[],
				}}
				usePagination={false}
				useExport={false}
				hasCheckboxes={false}
				manualSorting={false}
				useSearch={false}
				actions={[
					{
						label: 'Create Category',
						onClick: () => setAddCategoryOpen({ moduleId: module?.id }),
						type: 'primary',
					},
				]}
				rowActions={(row) => {
					return [
						...(!row?.isDefault
							? [
									{
										name: 'Delete',
										danger: `Are you sure you want to delete this category?`,
										confirmText: 'Delete category',
										onClick: (row) => {
											onDeleteCategory(
												{
													moduleId: module?.id,
													categoryId: row?.original?.id,
												},
												() =>
													onRefresh({ id: module?.id }, null, { silent: true })
											);
										},
									},
							  ]
							: []),
					];
				}}
			/>

			<Modal
				title="Are you sure you want to remove this field?"
				open={isRemoveFieldOpen}
				onClose={() => setRemoveFieldOpen(false)}
			>
				<div className="flex justify-end">
					<Button
						onClick={() => {
							const categoryId = isRemoveFieldOpen?.categoryId;
							const fieldId = isRemoveFieldOpen?.fieldId;

							onRemoveField(
								{
									id: categoryId,
									fieldId,
								},
								() => {
									onRefresh({ id: module?.id }, null, { silent: true });
									setRemoveFieldOpen(false);
								}
							);
						}}
					>
						Remove
					</Button>
				</div>
			</Modal>

			<Modal
				title="Add field to category"
				open={isAddToCategoryOpen}
				onClose={() => setAddToCategoryOpen(false)}
			>
				<TemplateReactSelect
					options={availableFields?.map((field) => ({
						label: field?.label,
						value: field?.id,
					}))}
					onChange={(index, value) => {
						const categoryId = isAddToCategoryOpen?.id;
						const fieldId = value?.value;
						onAddField(
							{
								id: categoryId,
								fieldId,
							},
							() => {
								onRefresh({ id: module?.id }, null, { silent: true });
								setAddToCategoryOpen(false);
							}
						);
					}}
					skip={isAddToCategoryOpen?.skip}
				/>
			</Modal>

			<Modal
				title="Create a new field"
				open={isAddFieldOpen}
				onClose={() => setAddFieldOpen(false)}
			>
				<div className="py-3 space-y-4">
					<TemplateField
						name="Label"
						onChange={(dataIndex, value) =>
							setForm({
								...form,
								label: value,
								key: value.replace(/\s+/g, '-').toLowerCase(),
							})
						}
						value={form?.label}
					/>
					<TemplateField name="Key" disabled value={form?.key} />
					<TemplateDropdown
						name="Field Type"
						options={[{ label: 'Text', value: 'text' }]}
						placeholder="Select a field type"
					/>
					<TemplateBoolean name="Is searchable" />
					<TemplateBoolean name="Is required" />

					<div className="flex justify-end mt-3">
						<Button>Add Field</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default Categories;
